import { Controller } from "@hotwired/stimulus"

// Drives the Claims Coach conversation UI (app/views/coach/show.html.erb).
//
// Responsibilities:
//  - Enter-to-send: Enter submits the turbo_stream form; Shift+Enter = newline.
//  - Optimistic send: the user's message echoes into the pane immediately and the
//    input clears + locks, so the UI feels responsive during the round-trip.
//  - Thinking indicator: an animated "coach is thinking…" bubble shows while the
//    server generates the reply.
//  - On success, the server appends the canonical user + assistant messages, so the
//    optimistic placeholder and the thinking bubble are removed to avoid duplicates.
//  - On failure, the placeholders are removed, the input is restored with the typed
//    text, and an inline error note is shown — the user never loses their message.
//  - Auto-scroll: keeps the pane pinned to the latest message as content streams in.
//
// The message form is a turbo_stream form (data-chat-target="form"); Turbo handles
// the request/response/CSRF. This controller manages the optimistic UX around it.
export default class extends Controller {
  static targets = ["messages", "input", "form", "submit", "chips"]

  connect() {
    this.scrollToBottom()

    if (this.hasMessagesTarget) {
      this.observer = new MutationObserver(() => this.scrollToBottom())
      this.observer.observe(this.messagesTarget, { childList: true, subtree: true })
    }

    this.onSubmitStart = this.handleSubmitStart.bind(this)
    this.onSubmitEnd = this.handleSubmitEnd.bind(this)
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("turbo:submit-start", this.onSubmitStart)
      this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd)
    }

    // RL-114: when the mobile bottom nav switches back to the chat column, the
    // messages pane had scrollHeight 0 while hidden — re-pin to the bottom on
    // the next frame (the event is already dispatched inside requestAnimationFrame,
    // but the layout may still be settling, so re-pin defensively).
    this.onChatShown = () => this.scrollToBottom()
    this.element.addEventListener("panel-tabs:chat-shown", this.onChatShown)

    // RL-114 (frontend contract #5): before Turbo snapshots this page for its
    // back/forward cache, strip transient optimistic/thinking/error nodes and
    // re-enable the input, so a restored snapshot never shows stale send state.
    this.onBeforeCache = this.cleanupForCache.bind(this)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("turbo:submit-start", this.onSubmitStart)
      this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    }
    this.element.removeEventListener("panel-tabs:chat-shown", this.onChatShown)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
  }

  // Remove optimistic/thinking/error placeholders and re-enable the composer so
  // the cached snapshot is clean. Guards on target presence in case the shell
  // has changed between connect and the cache event.
  cleanupForCache() {
    this.removeOptimistic()
    this.removeThinkingIndicator()
    this.removeError()
    if (this.hasInputTarget) this.inputTarget.disabled = false
    if (this.hasSubmitTarget) this.submitTarget.disabled = false
    // RL-122: never freeze a snapshot mid-send — clear the in-flight lock and
    // reveal the server-rendered chips so a restored page is interactive.
    this.inFlight = false
    this.showChips()
  }

  // keydown->chat#handleKeydown on the textarea
  handleKeydown(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    if (event.isComposing) return // don't submit mid-IME composition

    event.preventDefault()
    if (!this.hasFormTarget) return
    if (this.inFlight) return // single in-flight send — swallow Enter while sending
    if (this.hasInputTarget && this.inputTarget.value.trim() === "") return
    this.submit()
  }

  // action->chat#sendSuggestion on a suggested-reply chip. Writes the chip's
  // label into the composer and submits through the SAME path as a typed send,
  // so it inherits the optimistic echo / thinking / error-restore machinery.
  sendSuggestion(event) {
    if (!this.hasFormTarget || !this.hasInputTarget) return
    if (this.inFlight) return // single in-flight send — ignore double-taps

    const suggestion = (event.params && event.params.suggestion) || ""
    if (suggestion.trim() === "") return

    this.inputTarget.value = suggestion
    this.submit()
  }

  // Set the synchronous in-flight lock BEFORE requestSubmit so a second
  // Enter/tap in the same tick (before turbo:submit-start fires) can't slip a
  // duplicate send through. Cleared in handleSubmitEnd. `pendingText` is a
  // single slot and `removeOptimistic()` is greedy, so overlapping sends would
  // corrupt the optimistic UI — this lock is load-bearing, not cosmetic.
  submit() {
    this.inFlight = true
    this.formTarget.requestSubmit()
  }

  handleSubmitStart() {
    const text = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    if (text === "") { this.inFlight = false; return }

    this.pendingText = text
    this.renderOptimisticUserMessage(text)
    this.renderThinkingIndicator()
    this.hideChips()

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.disabled = true
    }
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
    this.scrollToBottom()
  }

  handleSubmitEnd(event) {
    this.removeOptimistic()
    this.removeThinkingIndicator()

    if (this.hasInputTarget) this.inputTarget.disabled = false
    if (this.hasSubmitTarget) this.submitTarget.disabled = false

    const failed = event.detail && event.detail.success === false
    if (failed) {
      // Restore the user's text so it isn't lost, and surface an inline error.
      if (this.hasInputTarget && this.pendingText) this.inputTarget.value = this.pendingText
      this.renderError()
      // Symmetric with the text restore: bring the chips back so the homeowner
      // can retry. On success the server's update("chips", …) supplies the new
      // set, so we leave the container hidden until that stream lands.
      this.showChips()
    }
    this.pendingText = null
    this.inFlight = false
    if (this.hasInputTarget) this.inputTarget.focus()
    this.scrollToBottom()
  }

  hideChips() {
    if (this.hasChipsTarget) this.chipsTarget.hidden = true
  }

  showChips() {
    if (this.hasChipsTarget) this.chipsTarget.hidden = false
  }

  renderOptimisticUserMessage(text) {
    if (!this.hasMessagesTarget) return
    const wrap = document.createElement("div")
    wrap.dataset.optimistic = "true"
    wrap.className = "flex items-start justify-end opacity-70"
    wrap.innerHTML = `
      <div class="mr-3 max-w-[80%]">
        <div class="rg-bubble-me px-4 py-2">
          <div class="prose prose-sm prose-invert max-w-none">${this.escape(text)}</div>
        </div>
        <p class="mt-1 text-xs text-faint text-right">Sending…</p>
      </div>`
    this.messagesTarget.appendChild(wrap)
  }

  renderThinkingIndicator() {
    if (!this.hasMessagesTarget) return
    const wrap = document.createElement("div")
    wrap.dataset.thinking = "true"
    wrap.className = "flex items-start"
    wrap.setAttribute("aria-live", "polite")
    wrap.innerHTML = `
      <div class="flex-shrink-0">
        <div class="w-8 h-8 rounded-full bg-clay-600 flex items-center justify-center" role="img" aria-label="Claims Coach">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
      </div>
      <div class="ml-3 max-w-[80%]">
        <div class="rg-bubble-them px-4 py-3">
          <span class="sr-only">Your coach is thinking…</span>
          <span class="flex gap-1" aria-hidden="true">
            <span class="w-2 h-2 bg-clay-600 rounded-full rg-typing-dot" style="animation-delay:-0.3s"></span>
            <span class="w-2 h-2 bg-clay-600 rounded-full rg-typing-dot" style="animation-delay:-0.15s"></span>
            <span class="w-2 h-2 bg-clay-600 rounded-full rg-typing-dot"></span>
          </span>
        </div>
      </div>`
    this.messagesTarget.appendChild(wrap)
  }

  renderError() {
    if (!this.hasMessagesTarget) return
    // Only one error pill at a time — a retry that fails again should replace the
    // note, not stack a second one below it.
    this.removeError()
    const wrap = document.createElement("div")
    wrap.dataset.chatError = "true"
    wrap.className = "flex justify-center"
    // The Retry button re-sends the restored message through the same submit()
    // path (data-action binds even though this node is inserted dynamically —
    // it's a descendant of the controller element). type="button" so it never
    // implicitly submits the composer form.
    wrap.innerHTML = `
      <div class="inline-flex items-center gap-2 px-4 py-2 bg-ember-100 border border-ember-700/20 rounded-full text-sm text-ember-700">
        Something went wrong sending your message. Your text was restored.
        <button type="button" data-action="chat#retry" class="font-medium underline text-ember-700 hover:text-ember-700/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-700/40 rounded">Retry</button>
      </div>`
    this.messagesTarget.appendChild(wrap)
  }

  // action->chat#retry on the error pill's Retry button. Re-sends the message
  // that just failed (still in the composer, restored by handleSubmitEnd) via
  // the same submit() path, so it inherits the optimistic echo / thinking /
  // error-restore machinery and the single-in-flight lock.
  retry() {
    if (this.inFlight) return // a send is already going — ignore the tap
    if (!this.hasFormTarget) return
    if (this.hasInputTarget && this.inputTarget.value.trim() === "") return
    this.removeError()
    this.submit()
  }

  removeError() {
    this.messagesTarget?.querySelectorAll('[data-chat-error="true"]').forEach((el) => el.remove())
  }

  removeOptimistic() {
    this.messagesTarget?.querySelectorAll('[data-optimistic="true"]').forEach((el) => el.remove())
  }

  removeThinkingIndicator() {
    this.messagesTarget?.querySelectorAll('[data-thinking="true"]').forEach((el) => el.remove())
  }

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
